/**
 * Pure review model for the AI match-sheet import (EPIC-112, D5/D6).
 *
 * Invariant (never-guess): a value reaches the wizard only if it was
 * legible AND agreed (status "ok") or the user typed/picked it. Every other
 * value is a conflict that blocks `buildWizardFill`.
 */
import type {
  ExtractedSheet,
  PlayerMatch,
  SheetField,
  SheetImportExtraction,
  SheetKitField,
  SheetRow,
} from "./matchSheetImportApi";
import type { MatchTeamKit } from "../types";

export const SHEET_POSITIONS = ["GK", "CB", "CM", "ST"] as const;

export type RowSection = "starters" | "substitutes";

export interface RowEdits {
  number?: number | null;
  first_name?: string;
  surname_1?: string;
  surname_2?: string;
  position?: string;
  captain?: boolean;
}

export type RowResolution =
  | { kind: "none" }
  | {
      kind: "player";
      playerId: string;
      name: string;
    }
  | { kind: "create" }
  | { kind: "removed" };

export interface ReviewRowState {
  key: string;
  section: RowSection;
  source: SheetRow;
  edits: RowEdits;
  resolution: RowResolution;
  playerMatch: PlayerMatch;
  /** EPIC-116 D2: player_id created for this row by an earlier Continue attempt. */
  createdPlayerId?: string;
}

/** A roster player as the review needs it (TeamPlayer is structurally compatible). */
export interface RosterEntry {
  player_id: string;
  player_name?: string;
  jersey_number?: number | null;
  is_active?: boolean;
}

/** What the review knows about each sheet's team roster (EPIC-116 D1). */
export interface RosterContext {
  rosters: [RosterEntry[] | null, RosterEntry[] | null];
  failed: [boolean, boolean];
}

/** EPIC-113: only the shirt colour is read, shown and sent; shorts/socks are legacy-tolerated. */
export type KitPart = "shirt";
export const KIT_PARTS: KitPart[] = ["shirt"];

export interface ReviewSheetState {
  teamId: string | null;
  teamConfirmed: boolean;
  rows: ReviewRowState[];
  /** hex string = picked, null = explicitly no colour, absent = untouched. */
  kitEdits: Partial<Record<KitPart, string | null>>;
}

export interface HeaderEdits {
  date?: string | null;
  time?: string | null;
  season?: string | null;
  /** "" = explicitly no venue. */
  venueId?: string;
  competitionId?: string;
}

export interface ReviewState {
  extraction: SheetImportExtraction;
  sheets: [ReviewSheetState, ReviewSheetState];
  header: HeaderEdits;
  homeIndex: 0 | 1 | null;
  homeConfirmed: boolean;
}

export type ConflictScope =
  | "roster"
  | "clash"
  | "duplicate"
  | "team"
  | "home_away"
  | "date"
  | "time"
  | "season"
  | "venue"
  | "competition"
  | "kit"
  | "row";

export interface Conflict {
  id: string;
  scope: ConflictScope;
  sheet?: 0 | 1;
  rowKey?: string;
}

export type ChipKind =
  | "exact"
  | "candidates"
  | "none"
  | "unreadable"
  | "disagreement"
  | "invalid"
  | "cross_sheet";

// ── helpers ──────────────────────────────────────────────────────────────

export const normalizeName = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const asString = (field: SheetField): string =>
  field.value == null ? "" : String(field.value).trim();

const rowKey = (section: RowSection, row: number) => `${section}-${row}`;

function initialSheetState(sheet: ExtractedSheet): ReviewSheetState {
  const mk = (section: RowSection, rows: SheetRow[]): ReviewRowState[] =>
    rows.map((r) => ({
      key: rowKey(section, r.row),
      section,
      source: r,
      edits: {},
      resolution: { kind: "none" },
      playerMatch: r.player_match,
    }));
  return {
    // An exact team match is pre-selected but still needs the user's confirm.
    teamId: sheet.team_match.status === "exact" ? sheet.team_match.id : null,
    teamConfirmed: false,
    rows: [
      ...mk("starters", sheet.starters),
      ...mk("substitutes", sheet.substitutes),
    ],
    kitEdits: {},
  };
}

export function createReviewState(
  extraction: SheetImportExtraction,
): ReviewState {
  return {
    extraction,
    sheets: [
      initialSheetState(extraction.sheets[0]),
      initialSheetState(extraction.sheets[1]),
    ],
    header: {},
    homeIndex: extraction.home_suggestion ?? null,
    homeConfirmed: false,
  };
}

// ── effective row values ─────────────────────────────────────────────────

export function effectiveNumber(row: ReviewRowState): number | null {
  if (row.edits.number !== undefined) return row.edits.number;
  const f = row.source.number;
  if (f.status !== "ok") return null;
  const n = Number(f.normalized ?? f.value);
  return Number.isInteger(n) ? n : null;
}

function effectiveText(
  row: ReviewRowState,
  key: "first_name" | "surname_1" | "surname_2",
): string | undefined {
  const edit = row.edits[key];
  if (edit !== undefined) return edit.trim();
  const f = row.source[key];
  return f.status === "ok" ? asString(f) : undefined;
}

/** Name inputs for the light `match-players` re-match call. */
export function rowMatchInput(row: ReviewRowState): {
  row: number;
  first_name: string;
  surname_1: string | null;
  surname_2: string | null;
  number: number | null;
} {
  return {
    row: row.source.row,
    first_name: effectiveText(row, "first_name") ?? "",
    surname_1: effectiveText(row, "surname_1") || null,
    surname_2: effectiveText(row, "surname_2") || null,
    number: effectiveNumber(row),
  };
}

export function effectivePosition(row: ReviewRowState): string | undefined {
  if (row.edits.position !== undefined) return row.edits.position;
  const f = row.source.position;
  if (f.status !== "ok") return undefined;
  return String(f.normalized ?? f.value ?? "").toUpperCase() || undefined;
}

export function effectiveCaptain(row: ReviewRowState): boolean {
  return row.edits.captain ?? row.source.captain;
}

export function effectivePlayerId(row: ReviewRowState): string | null {
  if (row.resolution.kind === "player") return row.resolution.playerId;
  if (
    row.resolution.kind === "none" &&
    row.playerMatch.status === "exact" &&
    row.playerMatch.player_id
  ) {
    return row.playerMatch.player_id;
  }
  return null;
}

/** Player a row resolves to, counting a player created by an earlier attempt. */
export function resolvedPlayerId(row: ReviewRowState): string | null {
  const id = effectivePlayerId(row);
  if (id) return id;
  if (row.resolution.kind === "create" && row.createdPlayerId) {
    return row.createdPlayerId;
  }
  return null;
}

export function fullName(row: ReviewRowState): string {
  return [
    effectiveText(row, "first_name"),
    effectiveText(row, "surname_1"),
    effectiveText(row, "surname_2"),
  ]
    .filter((p): p is string => !!p)
    .join(" ");
}

// ── row issues ───────────────────────────────────────────────────────────

export function duplicateNumberKeys(sheet: ReviewSheetState): Set<string> {
  const byNumber = new Map<number, string[]>();
  for (const r of sheet.rows) {
    if (r.resolution.kind === "removed") continue;
    const n = effectiveNumber(r);
    if (n == null) continue;
    byNumber.set(n, [...(byNumber.get(n) ?? []), r.key]);
  }
  const dup = new Set<string>();
  byNumber.forEach((keys) => {
    if (keys.length > 1) keys.forEach((k) => dup.add(k));
  });
  return dup;
}

export interface ClashItem {
  rowKey: string;
  number: number;
  playerId: string;
  name: string;
}

/**
 * D1: rows about to be created whose number belongs to a different active
 * roster player. `rows` are only the create rows.
 */
export function findRosterClashes(
  rows: { key: string; number: number | null }[],
  roster: RosterEntry[],
): Map<string, ClashItem> {
  const out = new Map<string, ClashItem>();
  for (const r of rows) {
    if (r.number == null) continue;
    const hit = roster.find(
      (p) => p.is_active !== false && p.jersey_number === r.number,
    );
    if (hit) {
      out.set(r.key, {
        rowKey: r.key,
        number: r.number,
        playerId: hit.player_id,
        name: hit.player_name ?? hit.player_id,
      });
    }
  }
  return out;
}

export function sheetRosterClashes(
  sheet: ReviewSheetState,
  roster: RosterEntry[] | null,
): Map<string, ClashItem> {
  if (!roster) return new Map();
  return findRosterClashes(
    sheet.rows
      .filter((r) => r.resolution.kind === "create" && !r.createdPlayerId)
      .map((r) => ({ key: r.key, number: effectiveNumber(r) })),
    roster,
  );
}

/** D3: groups of 2+ live rows resolved to one player_id. */
export function duplicatePlayerGroups(
  sheet: ReviewSheetState,
): { playerId: string; rowKeys: string[] }[] {
  const by = new Map<string, string[]>();
  for (const r of sheet.rows) {
    if (r.resolution.kind === "removed") continue;
    const id = resolvedPlayerId(r);
    if (!id) continue;
    by.set(id, [...(by.get(id) ?? []), r.key]);
  }
  const out: { playerId: string; rowKeys: string[] }[] = [];
  by.forEach((rowKeys, playerId) => {
    if (rowKeys.length > 1) out.push({ playerId, rowKeys });
  });
  return out;
}

type FieldKey =
  | "number"
  | "first_name"
  | "surname_1"
  | "surname_2"
  | "position"
  | "flags";

/** The unresolved status of one row field, or null when resolved. */
export function fieldIssue(
  row: ReviewRowState,
  key: FieldKey,
  duplicates: Set<string>,
): ChipKind | null {
  const f = row.source[key];
  const edited =
    key === "flags"
      ? row.edits.captain !== undefined
      : row.edits[key] !== undefined;
  if (!edited && f.status !== "ok") {
    return f.status as ChipKind;
  }
  if (key === "number") {
    const n = effectiveNumber(row);
    if (n == null || n < 1 || n > 99 || duplicates.has(row.key)) {
      return "invalid";
    }
  }
  if (key === "first_name" || key === "surname_1") {
    if (!effectiveText(row, key)) return "invalid";
  }
  if (key === "position") {
    const p = effectivePosition(row);
    if (!p || !(SHEET_POSITIONS as readonly string[]).includes(p)) {
      return "invalid";
    }
  }
  return null;
}

const ROW_FIELDS: FieldKey[] = [
  "number",
  "first_name",
  "surname_1",
  "surname_2",
  "position",
  "flags",
];

const CHIP_PRIORITY: ChipKind[] = [
  "unreadable",
  "disagreement",
  "invalid",
  "cross_sheet",
];

/** True when the row still needs the player-match resolution step. */
export function needsPlayerResolution(row: ReviewRowState): boolean {
  if (row.resolution.kind !== "none") return false;
  return !(row.playerMatch.status === "exact" && row.playerMatch.player_id);
}

export function rowChips(
  row: ReviewRowState,
  duplicates: Set<string>,
): ChipKind[] {
  if (row.resolution.kind === "removed") return [];
  const chips: ChipKind[] = [];
  for (const f of ROW_FIELDS) {
    const issue = fieldIssue(row, f, duplicates);
    if (issue && !chips.includes(issue)) chips.push(issue);
  }
  chips.sort((a, b) => CHIP_PRIORITY.indexOf(a) - CHIP_PRIORITY.indexOf(b));
  chips.push(row.playerMatch.status);
  return chips;
}

export function rowHasBlockingConflict(
  row: ReviewRowState,
  duplicates: Set<string>,
): boolean {
  if (row.resolution.kind === "removed") return false;
  if (ROW_FIELDS.some((f) => fieldIssue(row, f, duplicates))) return true;
  return needsPlayerResolution(row);
}

// ── header ───────────────────────────────────────────────────────────────

function bothSheets(
  state: ReviewState,
  pick: (s: ExtractedSheet) => SheetField,
): SheetField[] {
  return state.extraction.sheets.map(pick);
}

function autoShared(
  state: ReviewState,
  field: "date" | "time",
): string | null | undefined {
  const fields = bothSheets(state, (s) => s.header[field]);
  if (state.extraction.cross_sheet.some((c) => c.field === field)) {
    return undefined;
  }
  if (fields.some((f) => f.status !== "ok")) return undefined;
  const vals = fields.map((f) =>
    f.normalized == null ? null : String(f.normalized),
  );
  if (vals[0] === vals[1]) return vals[0];
  // one sheet blank, the other filled: still agreed
  if (vals[0] == null) return vals[1];
  if (vals[1] == null) return vals[0];
  return undefined;
}

function autoSeason(state: ReviewState): string | null | undefined {
  const fields = bothSheets(state, (s) => s.header.season);
  const ok = fields.find((f) => f.status === "ok" && asString(f));
  if (ok) return asString(ok);
  if (fields.every((f) => f.status === "ok")) return null;
  return undefined;
}

function autoVenue(state: ReviewState): string | undefined {
  const fields = bothSheets(state, (s) => s.header.venue);
  const crossed = state.extraction.cross_sheet.some((c) => c.field === "venue");
  const vm = state.extraction.venue_match;
  if (!crossed && fields.every((f) => f.status === "ok")) {
    if (vm.status === "exact" && vm.id) return vm.id;
    if (fields.every((f) => !asString(f))) return "";
  }
  return undefined;
}

function autoCompetition(state: ReviewState): string | undefined {
  const fields = state.extraction.sheets.flatMap((s) => [
    s.header.tournament,
    s.header.category,
  ]);
  const cm = state.extraction.competition_match;
  if (
    cm.status === "exact" &&
    cm.id &&
    fields.every((f) => f.status === "ok")
  ) {
    return cm.id;
  }
  return undefined;
}

export interface ResolvedHeader {
  date?: string | null;
  time?: string | null;
  season?: string | null;
  venueId?: string;
  competitionId?: string;
}

export function resolveHeader(state: ReviewState): ResolvedHeader {
  const h = state.header;
  return {
    date: h.date !== undefined ? h.date : autoShared(state, "date"),
    time: h.time !== undefined ? h.time : autoShared(state, "time"),
    season: h.season !== undefined ? h.season : autoSeason(state),
    venueId: h.venueId !== undefined ? h.venueId : autoVenue(state),
    competitionId:
      h.competitionId !== undefined ? h.competitionId : autoCompetition(state),
  };
}

// ── kit ──────────────────────────────────────────────────────────────────

/** hex | null (resolved, no colour) | undefined (unresolved conflict). */
export function effectiveKitHex(
  sheet: ReviewSheetState,
  field: SheetKitField,
  part: KitPart,
): string | null | undefined {
  const edit = sheet.kitEdits[part];
  if (edit !== undefined) return edit;
  if (field.status !== "ok") return undefined;
  if (field.hex) return field.hex;
  return asString(field) ? undefined : null;
}

export function resolveKit(
  state: ReviewState,
  sheetIndex: 0 | 1,
): { kit: MatchTeamKit; unresolved: KitPart[] } {
  const sheet = state.sheets[sheetIndex];
  const header = state.extraction.sheets[sheetIndex].header.kit;
  const unresolved: KitPart[] = [];
  const hex: Partial<Record<KitPart, string | null>> = {};
  const labels: Partial<Record<KitPart, string | null>> = {};
  for (const part of KIT_PARTS) {
    const eff = effectiveKitHex(sheet, header[part], part);
    if (eff === undefined) unresolved.push(part);
    hex[part] = eff ?? null;
    labels[part] = asString(header[part]) || null;
  }
  return {
    unresolved,
    kit: {
      shirt_color: hex.shirt ?? null,
      labels: { shirt: labels.shirt ?? null },
    },
  };
}

// ── conflicts ────────────────────────────────────────────────────────────

export function computeConflicts(
  state: ReviewState,
  ctx?: RosterContext,
): Conflict[] {
  const out: Conflict[] = [];
  const [a, b] = state.sheets;
  ([0, 1] as const).forEach((i) => {
    const s = state.sheets[i];
    if (!s.teamId || !s.teamConfirmed) {
      out.push({ id: `team-${i}`, scope: "team", sheet: i });
    }
  });
  if (a.teamId && b.teamId && a.teamId === b.teamId) {
    out.push({ id: "team-same", scope: "team" });
  }
  if (state.homeIndex == null || !state.homeConfirmed) {
    out.push({ id: "home-away", scope: "home_away" });
  }
  const h = resolveHeader(state);
  if (h.date === undefined) out.push({ id: "date", scope: "date" });
  if (h.time === undefined) out.push({ id: "time", scope: "time" });
  if (h.season === undefined) out.push({ id: "season", scope: "season" });
  if (h.venueId === undefined) out.push({ id: "venue", scope: "venue" });
  if (!h.competitionId) out.push({ id: "competition", scope: "competition" });
  ([0, 1] as const).forEach((i) => {
    resolveKit(state, i).unresolved.forEach((part) =>
      out.push({ id: `kit-${i}-${part}`, scope: "kit", sheet: i }),
    );
    const sheet = state.sheets[i];
    if (ctx?.failed[i])
      out.push({ id: `roster-${i}`, scope: "roster", sheet: i });
    sheetRosterClashes(sheet, ctx?.rosters[i] ?? null).forEach((c) =>
      out.push({
        id: `clash-${i}-${c.rowKey}`,
        scope: "clash",
        sheet: i,
        rowKey: c.rowKey,
      }),
    );
    duplicatePlayerGroups(sheet).forEach((g) =>
      out.push({
        id: `dup-${i}-${g.playerId}`,
        scope: "duplicate",
        sheet: i,
        rowKey: g.rowKeys[0],
      }),
    );
    const dups = duplicateNumberKeys(sheet);
    sheet.rows.forEach((r) => {
      if (rowHasBlockingConflict(r, dups)) {
        out.push({
          id: `row-${i}-${r.key}`,
          scope: "row",
          sheet: i,
          rowKey: r.key,
        });
      }
    });
  });
  return out;
}

// ── wizard fill ──────────────────────────────────────────────────────────

export interface FillRow {
  key: string;
  isStarter: boolean;
  number: number;
  position: string;
  captain: boolean;
  fullName: string;
  /** null = provisional player to create on Continue. */
  playerId: string | null;
  /** Created by an earlier Continue attempt: linked, never created again. */
  createdPlayerId?: string;
  playerName: string;
  nameParts: { first: string; surname1: string; surname2: string };
}

export interface FillSide {
  sheetIndex: 0 | 1;
  teamId: string;
  kit: MatchTeamKit;
  lineup: FillRow[];
}

export interface WizardFill {
  competitionId: string;
  season: string;
  date: string;
  time: string;
  venueId: string;
  home: FillSide;
  away: FillSide;
}

function buildSide(state: ReviewState, i: 0 | 1): FillSide {
  const sheet = state.sheets[i];
  const lineup: FillRow[] = [];
  for (const r of sheet.rows) {
    if (r.resolution.kind === "removed") continue;
    const playerId = effectivePlayerId(r);
    lineup.push({
      key: r.key,
      isStarter: r.section === "starters",
      number: effectiveNumber(r) as number,
      position: effectivePosition(r) as string,
      captain: effectiveCaptain(r),
      fullName: fullName(r),
      playerId,
      createdPlayerId:
        playerId == null && r.resolution.kind === "create"
          ? r.createdPlayerId
          : undefined,
      playerName:
        r.resolution.kind === "player" ? r.resolution.name : fullName(r),
      nameParts: {
        first: effectiveText(r, "first_name") ?? "",
        surname1: effectiveText(r, "surname_1") ?? "",
        surname2: effectiveText(r, "surname_2") ?? "",
      },
    });
  }
  return {
    sheetIndex: i,
    teamId: sheet.teamId as string,
    kit: resolveKit(state, i).kit,
    lineup,
  };
}

/** Throws while any blocking conflict remains: nothing unresolved passes. */
export function buildWizardFill(
  state: ReviewState,
  ctx?: RosterContext,
): WizardFill {
  if (computeConflicts(state, ctx).length > 0) {
    throw new Error("Unresolved conflicts remain");
  }
  const h = resolveHeader(state);
  const homeIdx = state.homeIndex as 0 | 1;
  const awayIdx = (homeIdx === 0 ? 1 : 0) as 0 | 1;
  return {
    competitionId: h.competitionId as string,
    season: h.season ?? "",
    date: h.date ?? "",
    time: h.time ?? "",
    venueId: h.venueId ?? "",
    home: buildSide(state, homeIdx),
    away: buildSide(state, awayIdx),
  };
}

// ── state transitions ────────────────────────────────────────────────────

export function updateRow(
  state: ReviewState,
  sheetIndex: 0 | 1,
  key: string,
  patch: Partial<
    Pick<
      ReviewRowState,
      "edits" | "resolution" | "playerMatch" | "section" | "createdPlayerId"
    >
  >,
): ReviewState {
  const sheets = state.sheets.map((s, i) =>
    i !== sheetIndex
      ? s
      : {
          ...s,
          rows: s.rows.map((r) =>
            r.key === key
              ? {
                  ...r,
                  ...patch,
                  edits: patch.edits ? { ...r.edits, ...patch.edits } : r.edits,
                }
              : r,
          ),
        },
  ) as [ReviewSheetState, ReviewSheetState];
  return { ...state, sheets };
}

/** Changing the confirmed team invalidates every player resolution. */
export function applyTeamChange(
  state: ReviewState,
  sheetIndex: 0 | 1,
  teamId: string | null,
  matches: Record<number, PlayerMatch> | null,
): ReviewState {
  const sheets = state.sheets.map((s, i) =>
    i !== sheetIndex
      ? s
      : {
          ...s,
          teamId,
          teamConfirmed: false,
          rows: s.rows.map((r) => ({
            ...r,
            createdPlayerId: undefined,
            resolution:
              r.resolution.kind === "removed"
                ? r.resolution
                : ({ kind: "none" } as RowResolution),
            playerMatch: matches?.[r.source.row] ?? r.playerMatch,
          })),
        },
  ) as [ReviewSheetState, ReviewSheetState];
  return { ...state, sheets };
}

// ── sessionStorage (extraction + resolutions, never the images) ──────────

const STORAGE_KEY = "promatch.matchSheetImport.v1";

export function saveReviewState(state: ReviewState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable: review simply is not persisted */
  }
}

export function loadReviewState(): ReviewState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReviewState;
    if (!parsed?.extraction?.sheets || parsed.sheets?.length !== 2) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearReviewState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
